import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastRoot } from '@/components/atoms/Toast';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { useAuthBootstrap, useTg, useTgTheme } from '@/hooks';
import { ws } from '@/ws';
import { useAuthStore } from '@/store';
import {
  HomePage,
  CategoryPage,
  SpecialistProfilePage,
  SearchPage,
  ChatListPage,
  ChatThreadPage,
  OrderDetailPage,
  CartPage,
  MyProfilePage,
  MyProfileEditPage,
  MyPresetsPage,
  MyProductsPage,
  MyPortfolioPage,
  MyQAPage,
  MyCategoriesPage,
  MyCitiesPage,
  MyCalendarPage,
  MyClientCalendarPage,
  MyWorkingHoursPage,
  MyServiceAreaPage,
  MyStatsPage,
  MyCrmPage,
  VerificationPage,
  SavedSearchesPage,
  FavoritesPage,
  NotificationsPage,
  ReverseBoardPage,
  ReverseRequestDetailPage,
  ReverseRequestNewPage,
  SettingsPage,
  BlockedPage,
  NotFoundPage,
} from '@/pages';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

/**
 * Map a TG WebApp `start_param` (set by the bot via web_app deep links
 * such as `?startapp=chat_42`) to a SPA route. Returns null when the
 * param shape is unrecognised so the app stays where it is.
 */
function startParamToPath(param: string | undefined): string | null {
  if (!param) return null;
  const m = /^([a-z_]+)_(.+)$/i.exec(param);
  if (!m) return null;
  const prefix = m[1];
  const rest = m[2];
  if (!prefix || !rest) return null;
  const id = encodeURIComponent(rest);
  switch (prefix) {
    case 'chat':
      return `/chats/${id}`;
    case 'order':
      return `/orders/${id}`;
    case 'u':
      return `/u/${id}`;
    case 'cat':
      return `/c/${id}`;
    case 'reverse':
      return `/board/${id}`;
    default:
      return null;
  }
}

function AppShell(): JSX.Element {
  useAuthBootstrap();
  const tg = useTg();
  const colorScheme = useTgTheme();
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const startRoutedRef = useRef(false);

  useEffect(() => {
    if (!tg) return;
    try {
      tg.ready();
      tg.expand();
    } catch {
      // older TG versions
    }
  }, [tg]);

  // One-shot navigation on cold start when TG passed `start_param`.
  useEffect(() => {
    if (startRoutedRef.current) return;
    const param =
      tg?.initDataUnsafe?.start_param ??
      new URLSearchParams(window.location.search).get('tgWebAppStartParam') ??
      undefined;
    const target = startParamToPath(param ?? undefined);
    if (target && window.location.pathname === '/') {
      startRoutedRef.current = true;
      navigate(target, { replace: true });
    }
  }, [tg, navigate]);

  useEffect(() => {
    document.documentElement.dataset.theme = colorScheme;
  }, [colorScheme]);

  useEffect(() => {
    if (!user) return;
    ws.connect();
    return () => ws.disconnect();
  }, [user]);

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/c/:slug" element={<CategoryPage />} />
      <Route path="/c/:slug/:city" element={<CategoryPage />} />
      <Route path="/u/:slug" element={<SpecialistProfilePage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/search/saved" element={<SavedSearchesPage />} />
      {/* F-180 A5 — gate authenticated surfaces behind <RequireAuth>.
          Public catalog (/, /c/*, /u/*, /search*, /board*) stays open
          so anonymous web visitors can browse without the
          "Увійди через бота" wall. */}
      <Route path="/chats" element={<RequireAuth><ChatListPage /></RequireAuth>} />
      <Route path="/chats/:id" element={<RequireAuth><ChatThreadPage /></RequireAuth>} />
      {/* F-170 B2.10 — /orders merged into /chats; keep the route alive
          as a redirect so old TG deep-links still resolve. */}
      <Route path="/orders" element={<Navigate to="/chats" replace />} />
      <Route path="/orders/:id" element={<RequireAuth><OrderDetailPage /></RequireAuth>} />
      <Route path="/cart" element={<RequireAuth><CartPage /></RequireAuth>} />
      <Route path="/cart/:specialistId" element={<RequireAuth><CartPage /></RequireAuth>} />
      <Route path="/me" element={<RequireAuth><MyProfilePage /></RequireAuth>} />
      <Route path="/me/edit" element={<RequireAuth><MyProfileEditPage /></RequireAuth>} />
      <Route path="/me/presets" element={<RequireAuth><MyPresetsPage /></RequireAuth>} />
      {/* F-180 (B5): specialist product CRUD. */}
      <Route path="/me/products" element={<RequireAuth><MyProductsPage /></RequireAuth>} />
      <Route path="/me/portfolio" element={<RequireAuth><MyPortfolioPage /></RequireAuth>} />
      <Route path="/me/qa" element={<RequireAuth><MyQAPage /></RequireAuth>} />
      <Route path="/me/categories" element={<RequireAuth><MyCategoriesPage /></RequireAuth>} />
      <Route path="/me/cities" element={<RequireAuth><MyCitiesPage /></RequireAuth>} />
      <Route path="/me/calendar" element={<RequireAuth><MyCalendarPage /></RequireAuth>} />
      <Route path="/me/working-hours" element={<RequireAuth><MyWorkingHoursPage /></RequireAuth>} />
      <Route path="/me/service-area" element={<RequireAuth><MyServiceAreaPage /></RequireAuth>} />
      {/* F-170 B3.6: client-side calendar route. The cabinet routes to
          this for clients; specialists keep using /me/calendar. */}
      <Route path="/calendar" element={<RequireAuth><MyClientCalendarPage /></RequireAuth>} />
      <Route path="/me/stats" element={<RequireAuth><MyStatsPage /></RequireAuth>} />
      <Route path="/me/crm" element={<RequireAuth><MyCrmPage /></RequireAuth>} />
      <Route path="/me/verification" element={<RequireAuth><VerificationPage /></RequireAuth>} />
      <Route path="/me/blocked" element={<RequireAuth><BlockedPage /></RequireAuth>} />
      <Route path="/favorites" element={<RequireAuth><FavoritesPage /></RequireAuth>} />
      <Route path="/notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />
      <Route path="/board" element={<ReverseBoardPage />} />
      <Route path="/board/new" element={<ReverseRequestNewPage />} />
      <Route path="/board/:id" element={<ReverseRequestDetailPage />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppShell />
          <ToastRoot />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
